import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysSupplierRequest } from '../queryKeys';

import { fetchSupplierRequest } from './fetchSupplierRequest';
import { type SupplierRequestDetail } from './types';

/**
 * Generates the query options for fetching a single supplier request.
 *
 * @param reqId - The UUID of the supplier request (or undefined to skip).
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getSupplierRequestQueryOptions = <TData = SupplierRequestDetail>({
  reqId,
  options,
}: {
  reqId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<SupplierRequestDetail, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: reqId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchSupplierRequest(reqId, { signal })
      : skipToken,
    queryKey: queryKeysSupplierRequest.get(reqId),
    // The search agent enriches a request in the background, so a request opened
    // while it runs would otherwise show an empty researched column until a
    // manual reload.
    refetchInterval(query) {
      return query.state.data?.enrichmentStatus === 'running'
        ? ms('5s')
        : false;
    },
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for fetching a single supplier request by ID.
 * The query is skipped when no `reqId` is provided.
 *
 * @param reqId - The UUID of the supplier request (or undefined to skip).
 * @param options - Additional TanStack Query options.
 * @returns The supplier request detail.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Requests/operation/get_request_vadmin_supplier_requests__req_id__get
 */
export const useQuerySupplierRequest = <TData = SupplierRequestDetail>(
  reqId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SupplierRequestDetail, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getSupplierRequestQueryOptions<TData>({ reqId, options }));
